import { BannerData } from "types/api/layout";

type Banner = BannerData["banner"];

export type BannerSource = {
  minWidth: number;
  srcSet: string;
  type?: "image/png" | "image/svg+xml" | "image/webp";
};

const imageTypeFor = (url: string): BannerSource["type"] => {
  const path = url.split(/[?#]/)[0].toLowerCase();
  if (path.endsWith(".png")) return "image/png";
  if (path.endsWith(".svg")) return "image/svg+xml";
  if (path.endsWith(".webp")) return "image/webp";
  return undefined;
};

const BANNER_BREAKPOINTS: Array<{
  minWidth: number;
  keys: Array<keyof Banner>;
}> = [
  {
    minWidth: 1440,
    keys: [
      "image_xx_large_png",
      "external_image_xx_large_png",
      "image_xx_large",
      "external_image_xx_large",
    ],
  },
  {
    minWidth: 1240,
    keys: [
      "image_x_large_png",
      "external_image_x_large_png",
      "image_x_large",
      "external_image_x_large",
    ],
  },
  {
    minWidth: 905,
    keys: [
      "image_large_png",
      "external_image_large_png",
      "image_large",
      "external_image_large",
    ],
  },
  {
    minWidth: 600,
    keys: [
      "image_medium_png",
      "external_image_medium_png",
      "image_medium",
      "external_image_medium",
    ],
  },
  {
    minWidth: 360,
    keys: [
      "image_small_png",
      "external_image_small_png",
      "image_small",
      "external_image_small",
    ],
  },
  {
    minWidth: 0,
    keys: [
      "image_x_small_png",
      "external_image_x_small_png",
      "image_x_small",
      "external_image_x_small",
      "image_small_png",
      "external_image_small_png",
      "image_small",
      "external_image_small",
    ],
  },
];

const firstSource = (banner: Banner, keys: Array<keyof Banner>) => {
  for (const key of keys) {
    const value = banner[key];
    if (typeof value !== "string" || !value) continue;
    return {
      srcSet: value,
      type: imageTypeFor(value),
    };
  }
  return null;
};

export const getAvailableSources = (banner: Banner): BannerSource[] => {
  const sources: BannerSource[] = [];
  for (const { minWidth, keys } of BANNER_BREAKPOINTS) {
    const source = firstSource(banner, keys);
    if (!source || sources[sources.length - 1]?.srcSet === source.srcSet)
      continue;
    sources.push({ minWidth, ...source });
  }
  return sources;
};

export const preloadMediaFor = (sources: BannerSource[], index: number) => {
  if (!sources || !Array.isArray(sources)) return undefined;

  const isFirst = index === 0;
  const isLast = index === sources.length - 1;

  if (isFirst && isLast) return undefined;

  if (isFirst) return `(min-width: ${sources[index].minWidth}px)`;

  const maxWidth = sources[index - 1].minWidth - 1;
  if (isLast) return `(max-width: ${maxWidth}px)`;

  return `(min-width: ${sources[index].minWidth}px) and (max-width: ${maxWidth}px)`;
};

export const getImageOrigin = (url: string) => {
  try {
    return new URL(url).origin;
  } catch {
    return "";
  }
};
